import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import OverlayLoader from '../../components/overlayLoader';
import FormInput from '../../components/forms/formInput';
import PhoneInput from '../../components/forms/phoneInput';
import CountrySelect from '../../components/selects/countrySelect';
import { createClient, getClientById, updateClient, uploadClientAvatar, validateAddress } from '../../services/clients';
import AvatarUpload from '../../components/avatarUpload';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { rsdFormat, trimProps } from '../../utils/helper';
import { normalizePhone } from '../../utils/phone';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const UserIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);

/* card-style form to match the client detail dashboard (cset-*) */
const cardStyles = `
  .sbr .cset-hero{display:flex;align-items:center;gap:16px;padding:2px 2px 18px}
  .sbr .cset-htx{min-width:0}
  .sbr .cset-nm{font-size:18px;font-weight:800;color:var(--ink);letter-spacing:-.4px}
  .sbr .cset-hint{font-size:11.5px;color:var(--brand-ink);font-weight:600;margin-top:5px}
  .sbr .cset-card{background:#fff;border:1px solid var(--line);border-radius:var(--r-md);padding:15px 16px;margin-bottom:14px;box-shadow:var(--sh-1)}
  .sbr .cset-card-h{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin-bottom:12px}
  .sbr .cset-tot{display:flex;align-items:center;justify-content:space-between;gap:12px}
  .sbr .cset-tot .l{font-size:11px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:var(--muted)}
  .sbr .cset-tot .v{font-size:15px;font-weight:800;color:var(--ink);font-variant-numeric:tabular-nums}
  .sbr .cset-seg{display:flex;gap:4px;background:var(--bg,#f1f3f5);border:1px solid var(--line);border-radius:var(--r-md);padding:4px;margin-bottom:14px}
  .sbr .cset-seg button{flex:1;border:0;background:transparent;border-radius:calc(var(--r-md) - 4px);padding:7px 10px;font-size:12.5px;font-weight:700;color:var(--muted);cursor:pointer}
  .sbr .cset-seg button.on{background:#fff;color:var(--ink);box-shadow:var(--sh-1)}
  .sbr .cset-geo-warn{display:flex;gap:12px;padding:12px 14px;margin-bottom:14px;background:var(--bad-bg,#FEF2F2);border:1px solid var(--bad,#DC2626)22;border-radius:var(--r-md);animation:cset-warn-in .25s ease}
  .sbr .cset-geo-warn-icon{flex-shrink:0;color:var(--bad,#DC2626);margin-top:1px}
  .sbr .cset-geo-warn-body{min-width:0}
  .sbr .cset-geo-warn-title{font-size:13px;font-weight:800;color:var(--bad,#DC2626);margin-bottom:3px}
  .sbr .cset-geo-warn-text{font-size:12.5px;font-weight:500;color:var(--ink);line-height:1.5;margin-bottom:10px}
  .sbr .cset-geo-warn-actions{display:flex;gap:8px}
  @keyframes cset-warn-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
`;
const initClient = {
    clientType: 'INDIVIDUAL',
    companyName: '',
    registrationNumber: '',
    taxNumber: '',
    avatarUrl: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Serbia',
    apartmentNo: '',
    floor: '',
    intercomName: '',
    notes: '',
    totalSQM: '',
    totalAmount: '',
}

const SetClientModal = ({
    display,
    toggle,
    clientId,
    onCreateClient,
    suggestName,
    asDrawer = false
}) => {
    const { t } = useTranslation();
    const formId = `set-client-${clientId ?? 'new'}`

    const handleChange = (objValue) => {
        const newClient = { ...client, ...objValue }
        setClient(newClient)
        if (geocodeWarning && ['street', 'city', 'state', 'zipCode', 'country'].some(k => k in objValue)) {
            setGeocodeWarning(null)
        }
    }

    const clientObj = initClient
    const suggestNameSplits = suggestName ? suggestName.split(' ') : []
    if (suggestNameSplits.length > 0) {
        clientObj.firstName = suggestNameSplits[0]
        if (suggestNameSplits.length > 1) clientObj.lastName = suggestNameSplits[1]
    }
    const [client, setClient] = useState(clientObj)
    const [pendingAvatar, setPendingAvatar] = useState(null)   // file picked before a new client exists
    const [uploadingAvatar, setUploadingAvatar] = useState(false)
    const [openSeq, setOpenSeq] = useState(0)                  // remounts the avatar picker on each open
    const [geocodeWarning, setGeocodeWarning] = useState(null) // 'approximate' | 'failed' | null
    const [validating, setValidating] = useState(false)

    const onShow = () => {
        !clientId && setClient(initClient)
        setPendingAvatar(null)
        setGeocodeWarning(null)
        setValidating(false)
        setOpenSeq((s) => s + 1)
    }


    const onHide = () => {
        toggle(false)
    }

    const queryClient = useQueryClient()

    const { mutate, isLoading } = useMutation(clientId ? updateClient : createClient, {
        onSuccess: onMutateSuccess(async (response) => {
            if (!clientId && pendingAvatar && response?.id) {
                setUploadingAvatar(true)
                try { await uploadClientAvatar(response.id, pendingAvatar) } catch (e) { /* non-fatal */ }
                setUploadingAvatar(false)
            }
            queryClient.invalidateQueries(['clients'])
            queryClient.invalidateQueries(['clients-option'])
            onCreateClient && onCreateClient(response)
            setPendingAvatar(null)
            setGeocodeWarning(null)
            onHide()
        })
    })

    const { data, isFetching } = useQuery(['clients', 'details', clientId], () => clientId && getClientById(clientId), {
        staleTime: 1000 * 60 * 2,
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            const _client = trimProps(result, initClient, ['id'])
            setClient(_client)
        })
    }, [data])

    const buildPayload = () => {
        const payload = client.clientType === 'COMPANY'
            ? { ...client }
            : { ...client, companyName: '', registrationNumber: '', taxNumber: '' }
        payload.phone = normalizePhone(client.phone) ?? client.phone
        return payload
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const payload = buildPayload()

        // Check the address against the geocoder before saving. If the
        // address can't be located exactly, show a warning and let the user
        // decide whether to save anyway or fix the address first.
        setValidating(true)
        try {
            const res = await validateAddress(payload)
            const result = res?.code === 200 ? res.message?.geocodeResult : null
            if (result && result !== 'exact') {
                setGeocodeWarning(result)
                return
            }
        } catch { /* network error — let the save attempt handle it */ }
        finally { setValidating(false) }

        mutate(payload)
    }

    const saveAnyway = () => {
        setGeocodeWarning(null)
        mutate(buildPayload())
    }

    const handleSelectChange = (prop) => (value) => {
        handleChange({ [prop]: value })
    }

    const handleAvatar = async (file) => {
        if (!clientId) return
        const res = await uploadClientAvatar(clientId, file)
        if (res?.code === 200 && res.message?.avatarUrl) {
            setClient((c) => ({ ...c, avatarUrl: res.message.avatarUrl }))
        } else {
            throw new Error(t('avatarUpload.uploadFailed'))
        }
    }

    const fullName = `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim()
    const isCompany = client.clientType === 'COMPANY'
    const heroName = isCompany ? (client.companyName || fullName) : fullName

    const clientFields = (
        <>
            {/* Individual / Company selector */}
            <div className="cset-seg" role="tablist" aria-label={t('clients.clientType')}>
                <button type="button" className={!isCompany ? 'on' : ''}
                    onClick={() => handleChange({ clientType: 'INDIVIDUAL' })}>{t('clients.individual')}</button>
                <button type="button" className={isCompany ? 'on' : ''}
                    onClick={() => handleChange({ clientType: 'COMPANY' })}>{t('clients.company')}</button>
            </div>

            {/* hero — mirrors the client detail dashboard */}
            <div className="cset-hero">
                <AvatarUpload
                    key={`${openSeq}-${isCompany ? 'c' : 'i'}`}
                    square size={66}
                    name={heroName}
                    src={client.avatarUrl}
                    onUpload={clientId ? handleAvatar : undefined}
                    onSelect={clientId ? undefined : setPendingAvatar}
                    uploadLabel={isCompany ? t('clients.uploadLogo') : t('clients.uploadPhoto')}
                    changeLabel={isCompany ? t('clients.changeLogo') : t('clients.changePhoto')}
                />
                {!clientId &&
                    <div className="cset-htx">
                        <div className="cset-nm">{heroName || (isCompany ? t('clients.newCompany') : t('clients.newClientLabel'))}</div>
                        <div className="cset-hint">{pendingAvatar
                            ? (isCompany ? t('clients.logoWillBeSaved') : t('clients.photoWillBeSaved'))
                            : (isCompany ? t('clients.optionalLogo') : t('clients.optionalPhoto'))}</div>
                    </div>
                }
            </div>

            {isCompany &&
                <div className="cset-card">
                    <div className="cset-card-h">{t('clients.company')}</div>
                    <Row>
                        <Col lg="12">
                            <FormInput className="form-control mb-2" label={t('clients.companyNameLabel')} value={client.companyName ?? ''}
                                onChange={(e) => handleChange({ companyName: e.target.value })} required
                            />
                        </Col>
                        <Col lg="6">
                            <FormInput className="form-control mb-2" label={t('clients.registrationNumber')} value={client.registrationNumber ?? ''}
                                onChange={(e) => handleChange({ registrationNumber: e.target.value })}
                            />
                        </Col>
                        <Col lg="6">
                            <FormInput className="form-control mb-2" label={t('clients.taxVatNumber')} value={client.taxNumber ?? ''}
                                onChange={(e) => handleChange({ taxNumber: e.target.value })}
                            />
                        </Col>
                    </Row>
                </div>
            }

            <div className="cset-card">
                <div className="cset-card-h">{isCompany ? t('clients.contactPerson') : t('clients.contact')}</div>
                <Row>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.firstName')} value={client.firstName ?? ''}
                            onChange={(e) => handleChange({ firstName: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.lastName')} value={client.lastName ?? ''}
                            onChange={(e) => handleChange({ lastName: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <PhoneInput className="mb-2" label={t('clients.phoneNumber')} name="phone" value={client.phone ?? ''}
                            onChange={(phone) => handleChange({ phone })} required
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput type="email" className="form-control mb-2" label={t('clients.email')} value={client.email ?? ''}
                            onChange={(e) => handleChange({ email: e.target.value })}
                        />
                    </Col>
                </Row>
            </div>

            <div className="cset-card">
                <div className="cset-card-h">{t('clients.address')}</div>
                <Row>
                    <Col lg="12">
                        <FormInput className="form-control mb-2" label={t('clients.street')} value={client.street ?? ''}
                            onChange={(e) => handleChange({ street: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.apartmentNo')} value={client.apartmentNo ?? ''}
                            onChange={(e) => handleChange({ apartmentNo: e.target.value })}
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.floor')} value={client.floor ?? ''}
                            onChange={(e) => handleChange({ floor: e.target.value })}
                        />
                    </Col>
                    <Col lg="12">
                        <FormInput className="form-control mb-2" label={t('clients.intercomName')} value={client.intercomName ?? ''}
                            onChange={(e) => handleChange({ intercomName: e.target.value })}
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.city')} value={client.city ?? ''}
                            onChange={(e) => handleChange({ city: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.zip')} value={client.zipCode ?? ''}
                            onChange={(e) => handleChange({ zipCode: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <FormInput className="form-control mb-2" label={t('clients.state')} value={client.state ?? ''}
                            onChange={(e) => handleChange({ state: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <CountrySelect className="mb-2" label={t('clients.country')} value={client.country ?? ''}
                            onChange={handleSelectChange('country')}
                            placeholder={t('clients.selectCountry')}
                            required
                        />
                    </Col>
                </Row>
            </div>

            <div className="cset-card">
                <div className="cset-card-h">{t('clients.notes')}</div>
                <FormInput className="form-control" value={client.notes ?? ''}
                    onChange={(e) => handleChange({ notes: e.target.value })}
                    as="textarea" placeholder={t('clients.notesPlaceholder')}
                />
            </div>

            {clientId && (client.totalSQM || client.totalAmount) ?
                <div className="cset-card cset-tot">
                    <div><div className="l">{t('clients.totalSQM')}</div><div className="v">{client.totalSQM || '—'}</div></div>
                    <div><div className="l">{t('clients.totalAmount')}</div><div className="v">{rsdFormat(+client.totalAmount)}</div></div>
                </div>
                : null
            }

            {geocodeWarning && (
                <div className="cset-geo-warn">
                    <div className="cset-geo-warn-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    </div>
                    <div className="cset-geo-warn-body">
                        <div className="cset-geo-warn-title">
                            {geocodeWarning === 'approximate'
                                ? t('clients.addressNotLocatedExactly')
                                : t('clients.addressNotFound')}
                        </div>
                        <div className="cset-geo-warn-text">
                            {geocodeWarning === 'approximate'
                                ? t('clients.addressApproximateText')
                                : t('clients.addressNotFoundText')}
                        </div>
                        <div className="cset-geo-warn-actions">
                            <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={saveAnyway} disabled={isLoading}>
                                {isLoading ? t('common.saving') : t('clients.saveAnyway')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            secondary={asDrawer}
            backdrop={(isLoading || uploadingAvatar || validating) ? 'static' : true}
            title={clientId ? t('clients.editClientTitle') : t('clients.addClientTitle')}
            subtitle={t('clients.contactAndAddress')}
            icon={<UserIcon />}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading || uploadingAvatar || validating}>{t('common.cancel')}</button>
                    <button type='submit' form={formId} className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading || uploadingAvatar || validating}>
                        {validating ? t('clients.checkingAddress') : isLoading ? t('common.saving') : uploadingAvatar ? t('clients.uploadingPhoto') : (clientId ? t('clients.saveChanges') : t('clients.addClientTitle'))}
                    </button>
                </>
            }
        >
            <Form id={formId} onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <style>{cardStyles}</style>
                {clientFields}
                {isFetching && <OverlayLoader />}
            </Form>
        </SbrDrawer>
    )
}

export default SetClientModal